/** Contain-fit image px into print area mm (keeps aspect ratio). */
export function fitImageToPrintArea(
  widthPx: number,
  heightPx: number,
  areaWmm: number,
  areaHmm: number,
  fill = 1,
): { widthMm: number; heightMm: number } {
  const aspect = Math.max(1, widthPx) / Math.max(1, heightPx);
  const maxW = Math.max(1, areaWmm) * fill;
  const maxH = Math.max(1, areaHmm) * fill;
  let widthMm = maxW;
  let heightMm = widthMm / aspect;
  if (heightMm > maxH) {
    heightMm = maxH;
    widthMm = heightMm * aspect;
  }
  return {
    widthMm: Math.round(widthMm * 100) / 100,
    heightMm: Math.round(heightMm * 100) / 100,
  };
}

/**
 * Read natural pixel size without requiring CORS on the artwork host.
 * `fetch` + createImageBitmap fails when the CDN origin list omits the shop
 * (e.g. www vs apex); HTMLImageElement still exposes naturalWidth.
 */
export function probeImagePixelSize(url: string): Promise<{ widthPx: number; heightPx: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const widthPx = img.naturalWidth || img.width || 0;
      const heightPx = img.naturalHeight || img.height || 0;
      if (widthPx < 1 || heightPx < 1) {
        reject(new Error('image_size_unknown'));
        return;
      }
      resolve({ widthPx, heightPx });
    };
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = url;
  });
}

/** True when the object box aspect differs meaningfully from the bitmap. */
export function aspectMismatch(
  boxW: number,
  boxH: number,
  natW: number,
  natH: number,
  tol = 0.05,
): boolean {
  if (boxW < 1 || boxH < 1 || natW < 1 || natH < 1) return false;
  const aBox = boxW / boxH;
  const aNat = natW / natH;
  return Math.abs(aBox - aNat) / aNat > tol;
}
